import React, { useEffect, useRef, useState } from "react";
import "./horizonTable.css";
import { toast } from "react-toastify";

function SitesPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editSite, setEditSite] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [newSite, setNewSite] = useState({
    siteName: "",
    siteLocation: "",
    latitude: null,
    longitude: null,
    siteCodeOrIdentifier: "",
    jNumber: "",
  });
  const [sites, setSites] = useState([]);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [googleMapsReady, setGoogleMapsReady] = useState(false);
  const [placePredictions, setPlacePredictions] = useState([]);
  const [activeLocationField, setActiveLocationField] = useState(null);
  const [isPlacesLoading, setIsPlacesLoading] = useState(false);
  const suggestionsRef = useRef(null);
  const addLocationInputRef = useRef(null);
  const editLocationInputRef = useRef(null);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const pageSize = 10;
  const normalizedRole = (currentUserRole || "").toLowerCase();
  const canAddSite = normalizedRole === "manager" || normalizedRole === "admin";

  // Assigned workers per site (read-only display; assign only when creating Work Sheet)
  const [assignmentsList, setAssignmentsList] = useState([]);

  useEffect(() => {
    fetchSites();
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.role) {
        setCurrentUserRole(user.role);
      }
    } catch (error) {
      console.error("Error getting user role:", error);
    }
  }, []);

  useEffect(() => {
    if (!sites.length) return;
    fetchAllAssignments();
  }, [sites.length]);

  useEffect(() => {
    if (!googleMapsApiKey) return;
    if (window.google?.maps?.places) {
      setGoogleMapsReady(true);
      return;
    }
    const scriptId = "google-maps-places-script";
    const existing = document.getElementById(scriptId);
    if (existing) {
      existing.addEventListener("load", () => setGoogleMapsReady(true));
      return;
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setGoogleMapsReady(true);
    document.body.appendChild(script);
  }, [googleMapsApiKey]);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setPlacePredictions([]);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const query =
      activeLocationField === "new"
        ? (newSite.siteLocation || "").trim()
        : activeLocationField === "edit"
          ? (editSite?.siteLocation || "").trim()
          : "";
    if (!query || !googleMapsReady || !window.google?.maps?.places) {
      setPlacePredictions([]);
      setIsPlacesLoading(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      setIsPlacesLoading(true);
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: query,
          types: ["geocode"],
          componentRestrictions: { country: "gb" },
        },
        (predictions, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
            setPlacePredictions([]);
            setIsPlacesLoading(false);
            return;
          }
          setPlacePredictions(predictions);
          setIsPlacesLoading(false);
        }
      );
    }, 250);
    return () => clearTimeout(timer);
  }, [activeLocationField, newSite.siteLocation, editSite?.siteLocation, googleMapsReady]);

  async function fetchAllAssignments() {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + "/api/site-worker-assignments", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setAssignmentsList(data.data);
      }
    } catch (e) {
      console.error(e);
    }
  }

  function isDuplicateSiteName(name, excludeId) {
    const n = (name || "").trim().toLowerCase();
    if (!n) return false;
    return sites.some(
      (s) =>
        String(s._id) !== String(excludeId || "") &&
        (s.siteName || "").trim().toLowerCase() === n
    );
  }

  // Real site names carry voltages and addresses — "Moscow Road Grid 66/22/6.6kv",
  // "Energy House (+ Pacific House)". Stripping those characters silently rewrote
  // the name of any existing site that was opened and saved, so only the control
  // characters go.
  function sanitizeSiteName(val) {
    return String(val || "").replace(/[^A-Za-z0-9 /.,\-()&'+]+/g, "");
  }

  function sanitizeJNumber(val) {
    return String(val || "").replace(/[^A-Za-z0-9]+/g, "");
  }

  function isValidSiteName(val) {
    const s = (val || "").trim();
    if (!s) return false;
    return /^[A-Za-z0-9 /.,\-()&'+]+$/.test(s);
  }

  function isValidJNumber(val) {
    const s = (val || "").trim();
    if (!s) return true;
    return /^[A-Za-z0-9]+$/.test(s);
  }

  function closeAddModal() {
    setShowAddModal(false);
    setPlacePredictions([]);
    setActiveLocationField(null);
    setNewSite({
      siteName: "",
      siteLocation: "",
      latitude: null,
      longitude: null,
      siteCodeOrIdentifier: "",
      jNumber: "",
    });
  }

  function closeEditModal() {
    setShowEditModal(false);
    setPlacePredictions([]);
    setActiveLocationField(null);
    setEditSite(null);
  }

  async function selectPlace(prediction, targetField) {
    if (!window.google?.maps?.places) return;
    const service = new window.google.maps.places.PlacesService(document.createElement("div"));
    service.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["formatted_address", "geometry", "name"],
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
        const selectedLocation = place.formatted_address || place.name || prediction.description || "";
        const lat = place.geometry?.location?.lat ? place.geometry.location.lat() : null;
        const lng = place.geometry?.location?.lng ? place.geometry.location.lng() : null;
        if (targetField === "new") {
          setNewSite((prev) => ({
            ...prev,
            siteLocation: selectedLocation,
            latitude: lat,
            longitude: lng,
          }));
          addLocationInputRef.current?.focus();
        } else if (targetField === "edit") {
          setEditSite((prev) => ({
            ...prev,
            siteLocation: selectedLocation,
            latitude: lat,
            longitude: lng,
          }));
          editLocationInputRef.current?.focus();
        }
        setPlacePredictions([]);
      }
    );
  }

  function getAssignedNamesForSite(siteId) {
    return assignmentsList
      .filter((a) => (a.siteId?._id || a.siteId) === siteId)
      .map((a) => {
        const u = a.appUserId;
        return u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email : "";
      })
      .filter(Boolean)
      .join(", ") || "—";
  }

  async function fetchSites() {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication required. Please login again.");
        setLoading(false);
        return;
      }
      const res = await fetch(baseUrl + "/api/sites", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401) setError("Authentication failed. Please login again.");
        else if (res.status === 403) setError("You don't have permission to access this data.");
        else setError(`Failed to load sites. Server responded with ${res.status}.`);
        setSites([]);
        setLoading(false);
        return;
      }
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setSites(data.data);
        setError(null);
      } else {
        setSites([]);
        setError(data.message || "Failed to load sites.");
      }
    } catch (err) {
      console.error("Error fetching sites:", err);
      setSites([]);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!canAddSite) {
      toast.error("You don't have permission to import sites");
      return;
    }
    setImporting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(baseUrl + "/api/sites/import", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || "Sites imported!");
        fetchSites();
      } else {
        toast.error(data.message || "Import failed");
      }
    } catch {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleAdd = async () => {
    if (!canAddSite) {
      toast.error("You don't have permission to add sites");
      return;
    }
    if (!newSite.siteName || !newSite.siteName.trim()) {
      toast.error("Site name is required");
      return;
    }
    if (!isValidSiteName(newSite.siteName)) {
      toast.error("Site name contains an unsupported character");
      return;
    }
    if (!isValidJNumber(newSite.jNumber)) {
      toast.error("J number can contain only letters and numbers");
      return;
    }
    if (isDuplicateSiteName(newSite.siteName, null)) {
      toast.error("A site with this name already exists.");
      return;
    }
    const addButton = document.querySelector('.modal-footer button[type="button"]:last-child');
    if (addButton) {
      addButton.disabled = true;
      addButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Adding...';
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + "/api/sites", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newSite),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchSites();
        closeAddModal();
        toast.success("Site added!");
      } else {
        toast.error(data.message || "Failed to add");
      }
    } catch {
      toast.error("Failed to add");
    } finally {
      if (addButton) {
        addButton.disabled = false;
        addButton.innerHTML = "Submit";
      }
    }
  };

  const handleEdit = (site) => {
    setEditSite({ ...site, latitude: site?.latitude ?? null, longitude: site?.longitude ?? null });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editSite.siteName || !editSite.siteName.trim()) {
      toast.error("Site name is required");
      return;
    }
    if (!isValidSiteName(editSite.siteName)) {
      toast.error("Site name contains an unsupported character");
      return;
    }
    if (!isValidJNumber(editSite.jNumber)) {
      toast.error("J number can contain only letters and numbers");
      return;
    }
    if (isDuplicateSiteName(editSite.siteName, editSite._id)) {
      toast.error("A site with this name already exists.");
      return;
    }
    const saveButton = document.querySelector('.modal-footer button[type="button"]:last-child');
    if (saveButton) {
      saveButton.disabled = true;
      saveButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Saving...';
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/sites/${editSite._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editSite),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchSites();
        closeEditModal();
        toast.success("Site updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    } finally {
      if (saveButton) {
        saveButton.disabled = false;
        saveButton.innerHTML = "Save";
      }
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const deleteButton = document.querySelector('.modal-footer button.btn-danger');
    if (deleteButton) {
      deleteButton.disabled = true;
      deleteButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Deleting...';
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/sites/${deleteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchSites();
        toast.success("Deleted successfully!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      if (deleteButton) {
        deleteButton.disabled = false;
        deleteButton.innerHTML = "Delete";
      }
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const filtered = sites.filter((s) => {
    const fields = [s.siteName, s.siteLocation, s.siteCodeOrIdentifier, s.jNumber].map((val) =>
      (val || "").toLowerCase()
    );
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every((word) => fields.some((field) => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered
    .slice()
    .reverse()
    .slice((page - 1) * pageSize, page * pageSize);
  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-GB");
  };

  return (
    <>
      <div
        style={{
          background: "#ffffff",
          borderRadius: "20px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          padding: "24px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1a202c", margin: 0 }}>Sites</h2>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i
                className="fa fa-search"
                style={{ position: "absolute", left: "12px", color: "#8b1634", fontSize: "14px" }}
              />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: "10px 12px 10px 36px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  fontSize: "14px",
                  outline: "none",
                  width: "250px",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b1634")}
                onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
              />
            </div>
            {canAddSite && (
              <>
                <input
                  type="file"
                  ref={importInputRef}
                  accept=".xlsx,.xls,.csv"
                  style={{ display: "none" }}
                  onChange={handleImportFile}
                />
                <button
                  onClick={() => importInputRef.current?.click()}
                  disabled={importing}
                  title="Import sites from an Excel/CSV file"
                  style={{
                    background: "#fff",
                    color: "#8b1634",
                    fontWeight: "600",
                    padding: "10px 18px",
                    borderRadius: "12px",
                    border: "1px solid #8b1634",
                    cursor: importing ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    opacity: importing ? 0.7 : 1,
                  }}
                >
                  <i className={importing ? "fa fa-spinner fa-spin" : "fa fa-file-excel-o"} /> {importing ? "Importing..." : "Import Excel"}
                </button>
              </>
            )}
            {canAddSite && (
              <button
                onClick={() => setShowAddModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  fontWeight: "600",
                  padding: "10px 20px",
                  borderRadius: "12px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#6d1128")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#8b1634")}
              >
                <i className="fa fa-plus" /> Add
              </button>
            )}
          </div>
        </div>

        <div
          style={{
            overflowX: "auto",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.2fr 1.4fr 1.1fr 0.9fr 1fr 1fr 1.2fr",
              background: "#f7fafc",
              padding: "12px 16px",
              borderBottom: "1px solid #e2e8f0",
              gap: "16px",
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Site name</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Site location</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Site code / ID</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>J number</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Created Date</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Updated Date</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Actions</div>
          </div>

          <div>
            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #8b1634",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginBottom: "20px",
                  }}
                />
                <div style={{ color: "#8b1634", fontSize: "16px", fontWeight: "600" }}>Loading sites...</div>
              </div>
            ) : error ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div style={{ fontSize: "48px", color: "#e53e3e", marginBottom: "16px" }}>⚠️</div>
                <div style={{ color: "#e53e3e", fontSize: "16px", fontWeight: "600", marginBottom: "12px" }}>
                  Error Loading Data
                </div>
                <div style={{ color: "#718096", fontSize: "14px", textAlign: "center", maxWidth: "400px", marginBottom: "20px" }}>
                  {error}
                </div>
                <button
                  onClick={fetchSites}
                  style={{
                    background: "#8b1634",
                    color: "#fff",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#6d1128")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#8b1634")}
                >
                  <i className="fa fa-refresh" /> Try Again
                </button>
              </div>
            ) : paged.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div style={{ fontSize: "48px", color: "#cbd5e0", marginBottom: "16px" }}>📍</div>
                <div style={{ color: "#718096", fontSize: "16px", fontWeight: "600" }}>No sites found</div>
                {search && (
                  <div style={{ color: "#a0aec0", fontSize: "14px", marginTop: "8px" }}>
                    Try adjusting your search criteria
                  </div>
                )}
              </div>
            ) : (
              paged.map((s, idx) => (
                <div
                  key={s._id || idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.2fr 1.4fr 1.1fr 0.9fr 1fr 1fr 1.2fr",
                    padding: "16px",
                    borderBottom: "1px solid #f1f5f9",
                    gap: "16px",
                    alignItems: "center",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <div style={{ color: "#2d3748", fontSize: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span>{s.siteName || "-"}</span>
                    {s.isHeadquarters ? (
                      <span
                        title="Holiday, sick, college and rest days are booked to this site"
                        style={{ flexShrink: 0, fontSize: "10px", fontWeight: 700, color: "#3730a3", background: "#e0e7ff", border: "1px solid #c7d2fe", borderRadius: "999px", padding: "1px 8px" }}
                      >
                        HQ
                      </span>
                    ) : null}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{s.siteLocation || "-"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{s.siteCodeOrIdentifier || "-"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{s.jNumber || "-"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{formatDateTime(s.createdAt)}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{formatDateTime(s.updatedAt)}</div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <button
                      onClick={() => handleEdit(s)}
                      title="Edit"
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: "6px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0fdf4")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: "16px" }} />
                    </button>
                    {/* The head office holds every holiday, sick, college and rest day.
                        Deleting it would strand all of them, so the button is not offered
                        (the API refuses it too). */}
                    {s.isHeadquarters ? null : (
                      <button
                        onClick={() => handleDelete(s._id)}
                        title="Delete"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "6px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fef2f2")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: "16px" }} />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Add Modal */}
        {showAddModal && (
          <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: "1000px" }}>
              <div className="modal-content border-0 shadow-lg" style={{ maxWidth: "1000px", margin: "0 auto", borderRadius: "18px", overflow: "visible" }}>
                <div className="modal-header border-0" style={{ background: "linear-gradient(135deg, #8b1634 0%, #b42352 100%)", borderRadius: "18px 18px 0 0", padding: "14px 18px" }}>
                  <h5 className="modal-title" style={{ color: "#fff", fontWeight: 700, margin: 0 }}>Add Site</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={closeAddModal} />
                </div>
                <div className="modal-body" style={{ padding: "18px", background: "#f8fafc", overflow: "visible" }}>
                  <div className="row mb-2">
                    <div className="col-6 mb-2">
                      <label>Site name</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="fa fa-map-marker-alt" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          autoComplete="off"
                          value={newSite.siteName}
                          onChange={(e) => setNewSite({ ...newSite, siteName: sanitizeSiteName(e.target.value) })}
                          placeholder="Site name"
                        />
                      </div>
                    </div>
                    <div className="col-6 mb-2">
                      <label>Site location (address / coordinates)</label>
                      <div className="input-group position-relative" ref={suggestionsRef}>
                        <span className="input-group-text">
                          <i className="fa fa-location-dot" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          autoComplete="off"
                          value={newSite.siteLocation}
                          ref={addLocationInputRef}
                          onFocus={() => setActiveLocationField("new")}
                          onChange={(e) =>
                            setNewSite({
                              ...newSite,
                              siteLocation: e.target.value,
                              latitude: null,
                              longitude: null,
                            })
                          }
                          placeholder="Address or coordinates"
                        />
                        {activeLocationField === "new" && (
                          <div
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              zIndex: 2000,
                              background: "#fff",
                              border: "1px solid #e2e8f0",
                              borderTop: "none",
                              borderRadius: "0 0 10px 10px",
                              maxHeight: "300px",
                              overflowY: "auto",
                              boxShadow: "0 14px 30px rgba(2, 6, 23, 0.16)",
                            }}
                          >
                            {isPlacesLoading && (
                              <div style={{ padding: "10px 12px", color: "#718096", fontSize: "13px" }}>
                                Loading locations...
                              </div>
                            )}
                            {!isPlacesLoading &&
                              placePredictions.map((prediction) => (
                                <button
                                  key={prediction.place_id}
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => selectPlace(prediction, "new")}
                                  style={{
                                    width: "100%",
                                    border: "none",
                                    background: "transparent",
                                    textAlign: "left",
                                    padding: "10px 12px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "10px",
                                  }}
                                >
                                  <i className="fa fa-location-dot" style={{ color: "#5a5a5a", marginTop: "4px" }} />
                                  <div>
                                    <div style={{ fontWeight: 600, color: "#2d3748", fontSize: "14px" }}>
                                      {prediction.structured_formatting?.main_text || prediction.description}
                                    </div>
                                    <div style={{ color: "#718096", fontSize: "12px" }}>
                                      {prediction.structured_formatting?.secondary_text || ""}
                                    </div>
                                  </div>
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-6 mb-2">
                      <label>Site code or identifier</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="fa fa-hashtag" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          autoComplete="off"
                          value={newSite.siteCodeOrIdentifier}
                          onChange={(e) => setNewSite({ ...newSite, siteCodeOrIdentifier: e.target.value })}
                          placeholder="Site code or identifier"
                        />
                      </div>
                    </div>
                    <div className="col-6 mb-2">
                      <label>J number</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="fa fa-sort-numeric-down" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          autoComplete="off"
                          value={newSite.jNumber}
                          onChange={(e) => setNewSite({ ...newSite, jNumber: sanitizeJNumber(e.target.value) })}
                          placeholder="J number"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0" style={{ background: "#fff", padding: "12px 18px", borderRadius: "0 0 18px 18px" }}>
                  <button type="button" className="btn btn-light border" style={{ fontWeight: 600 }} onClick={closeAddModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn"
                    style={{ background: "linear-gradient(135deg, #8b1634 0%, #b42352 100%)", color: "#fff", fontWeight: 700, border: "none", minWidth: "110px" }}
                    onClick={handleAdd}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editSite && (
          <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: "1000px" }}>
              <div className="modal-content border-0 shadow-lg" style={{ maxWidth: "1000px", margin: "0 auto", borderRadius: "18px", overflow: "visible" }}>
                <div className="modal-header border-0" style={{ background: "linear-gradient(135deg, #8b1634 0%, #b42352 100%)", borderRadius: "18px 18px 0 0", padding: "14px 18px" }}>
                  <h5 className="modal-title" style={{ color: "#fff", fontWeight: 700, margin: 0 }}>Edit Site</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={closeEditModal} />
                </div>
                <div className="modal-body" style={{ padding: "18px", background: "#f8fafc", overflow: "visible" }}>
                  <div className="row mb-2">
                    <div className="col-6 mb-2">
                      <label>Site name</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="fa fa-map-marker-alt" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          autoComplete="off"
                          value={editSite.siteName || ""}
                          onChange={(e) => setEditSite({ ...editSite, siteName: sanitizeSiteName(e.target.value) })}
                          placeholder="Site name"
                        />
                      </div>
                    </div>
                    <div className="col-6 mb-2">
                      <label>Site location (address / coordinates)</label>
                      <div className="input-group position-relative" ref={suggestionsRef}>
                        <span className="input-group-text">
                          <i className="fa fa-location-dot" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          autoComplete="off"
                          ref={editLocationInputRef}
                          value={editSite.siteLocation || ""}
                          onFocus={() => setActiveLocationField("edit")}
                          onChange={(e) =>
                            setEditSite({
                              ...editSite,
                              siteLocation: e.target.value,
                            })
                          }
                          placeholder="Address or coordinates"
                        />
                        {activeLocationField === "edit" && (
                          <div
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              zIndex: 2000,
                              background: "#fff",
                              border: "1px solid #e2e8f0",
                              borderTop: "none",
                              borderRadius: "0 0 10px 10px",
                              maxHeight: "300px",
                              overflowY: "auto",
                              boxShadow: "0 14px 30px rgba(2, 6, 23, 0.16)",
                            }}
                          >
                            {isPlacesLoading && (
                              <div style={{ padding: "10px 12px", color: "#718096", fontSize: "13px" }}>
                                Loading locations...
                              </div>
                            )}
                            {!isPlacesLoading &&
                              placePredictions.map((prediction) => (
                                <button
                                  key={prediction.place_id}
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => selectPlace(prediction, "edit")}
                                  style={{
                                    width: "100%",
                                    border: "none",
                                    background: "transparent",
                                    textAlign: "left",
                                    padding: "10px 12px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "10px",
                                  }}
                                >
                                  <i className="fa fa-location-dot" style={{ color: "#5a5a5a", marginTop: "4px" }} />
                                  <div>
                                    <div style={{ fontWeight: 600, color: "#2d3748", fontSize: "14px" }}>
                                      {prediction.structured_formatting?.main_text || prediction.description}
                                    </div>
                                    <div style={{ color: "#718096", fontSize: "12px" }}>
                                      {prediction.structured_formatting?.secondary_text || ""}
                                    </div>
                                  </div>
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-6 mb-2">
                      <label>Site code or identifier</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="fa fa-hashtag" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          autoComplete="off"
                          value={editSite.siteCodeOrIdentifier || ""}
                          onChange={(e) => setEditSite({ ...editSite, siteCodeOrIdentifier: e.target.value })}
                          placeholder="Site code or identifier"
                        />
                      </div>
                    </div>
                    <div className="col-6 mb-2">
                      <label>J number</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="fa fa-sort-numeric-down" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          autoComplete="off"
                          value={editSite.jNumber || ""}
                          onChange={(e) => setEditSite({ ...editSite, jNumber: sanitizeJNumber(e.target.value) })}
                          placeholder="J number"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0" style={{ background: "#fff", padding: "12px 18px", borderRadius: "0 0 18px 18px" }}>
                  <button type="button" className="btn btn-light border" style={{ fontWeight: 600 }} onClick={closeEditModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn"
                    style={{ background: "linear-gradient(135deg, #8b1634 0%, #b42352 100%)", color: "#fff", fontWeight: 700, border: "none", minWidth: "110px" }}
                    onClick={handleEditSave}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && (
          <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ maxWidth: "1200px", margin: "0 auto" }}>
                <div className="modal-header">
                  <h5 className="modal-title">Delete Site</h5>
                  <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete this site?</p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-danger" onClick={confirmDelete}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginTop: "18px",
              gap: "12px",
            }}
          >
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: page === 1 ? "#f7fafc" : "#ffffff",
                color: page === 1 ? "#a0aec0" : "#8b1634",
                cursor: page === 1 ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Prev
            </button>
            <span style={{ fontWeight: "700", color: "#8b1634", fontSize: "16px" }}>
              {page} / {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: page === totalPages ? "#f7fafc" : "#ffffff",
                color: page === totalPages ? "#a0aec0" : "#8b1634",
                cursor: page === totalPages ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default SitesPage;
